var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OrbTypes } from './OrbTypes';
import { Orb } from './Orb';
import * as THREE from 'three';
import * as TextCreation from '../services/creation/TextCreation';
import * as SystemAPI from '../core/SystemAPI';
import { global } from '../core/GlobalState';
/**
 * The OrbFactory is responsible for:
 * - Defining a collection of known orb configurations (`orbConfigs`).
 * - Creating orbs of various types based on a given configuration.
 * - Fetching and constructing orbs dynamically from various APIs.
 */
export class OrbFactory {
    //does this belong on the orb itself?? - seems pretty core but not sure
    //todo make it take in an OrbTypes object
    /**
     * **Create a new orb based on type**
     * - Uses a predefined style configuration from `orbConfigs`
     * - Assigns optional JSON metadata and a text label
     * @param orbType - The type of orb to create (e.g., 'GITHUB_ORB')
     * @param labelText - Optional label to display on the orb
     * @param jsonData - Optional metadata to store in the orb
     * @returns A new `Orb` instance or undefined
     */
    static createOrbByType(orbType, labelText, jsonData = {}) {
        var _a;
        const rawConfig = OrbTypes[orbType];
        if (!rawConfig) {
            console.warn(`Invalid orb type: "${orbType}"`, { orbType, labelText, jsonData });
            return undefined;
        }
        // Normalize
        // Clone the config before modifying it - Not doing this resulted in a bug where only one orb had text at a time
        const config = Object.assign({}, rawConfig);
        //TODO add support here for the text sprites to ahve their own detailed configs passed
        //if (labelText) {
        //    config.text = TextCreation.createTextSprite(labelText, 3, undefined, 10, undefined, '#f0f0f0', '#f0f0f0');
        //}
        if (labelText) {
            config.text = TextCreation.createTextSprite(labelText, (_a = config.textCanvasScale) !== null && _a !== void 0 ? _a : 3, undefined, 10, undefined, config.textColor ? `#${config.textColor.toString(16).padStart(6, '0')}` : '#f0f0f0', config.textOutlineColor ? `#${config.textOutlineColor.toString(16).padStart(6, '0')}` : '#f0f0f0');
        }
        const newOrb = new Orb(config, jsonData, labelText);
        if (!newOrb.orbConfig) {
            console.warn("Created orb missing styleConfig:", newOrb);
            return undefined;
        }
        if (rawConfig.actions) {
            for (const action of rawConfig.actions) {
                newOrb.addAction(action);
            }
        }
        return newOrb;
    }
    /**
     * **Shared Function for Creating Orbs from API Data**
     * - Converts raw API data into structured orbs.
     * - Each API should pass a custom `parseFunction()` that extracts `orbType`, `label`, and `data`.
     * @param rawData - The unparsed API response array
     * @param parseFunction - A function that extracts necessary orb details
     * @returns An array of newly created orbs
     */
    static createOrbsFromData(rawData, parseFunction) {
        return __awaiter(this, void 0, void 0, function* () {
            const orbs = [];
            const dataArray = Array.isArray(rawData)
                ? rawData
                : Array.isArray(rawData === null || rawData === void 0 ? void 0 : rawData.data)
                    ? rawData.data
                    : Array.isArray(rawData === null || rawData === void 0 ? void 0 : rawData.payload)
                        ? rawData.payload
                        : [];
            if (!Array.isArray(dataArray)) {
                console.warn('createOrbsFromData: Expected array payload, received', rawData);
                return orbs;
            }
            const MAX_ITEMS_SAFE_MODE = 320;
            const CHUNK_SIZE = 40;
            const FRAME_BUDGET_MS = 6;
            let items = dataArray;
            if (global.safeModeEnabled && items.length > MAX_ITEMS_SAFE_MODE) {
                console.warn('[OrbFactory] Safe mode truncating large payload', {
                    total: items.length,
                    max: MAX_ITEMS_SAFE_MODE,
                });
                items = items.slice(0, MAX_ITEMS_SAFE_MODE);
            }
            const now = () => {
                if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
                    return performance.now();
                }
                return Date.now();
            };
            const yieldToMain = () => new Promise((resolve) => {
                if (typeof requestAnimationFrame === 'function') {
                    requestAnimationFrame(() => resolve());
                }
                else {
                    setTimeout(resolve, 0);
                }
            });
            try {
                let sliceStart = now();
                for (let index = 0; index < items.length; index += 1) {
                    const item = items[index];
                    try {
                        const { orbType, label, data } = parseFunction(item);
                        const orb = this.createOrbByType(orbType, label, data);
                        if (orb) {
                            orbs.push(orb);
                        }
                    }
                    catch (error) {
                        console.warn("Skipping invalid orb:", item, error);
                    }
                    if ((index + 1) % CHUNK_SIZE === 0 || now() - sliceStart > FRAME_BUDGET_MS) {
                        yield yieldToMain();
                        sliceStart = now();
                    }
                }
            }
            catch (iterationError) {
                console.error('createOrbsFromData: Failed to iterate response', iterationError);
            }
            return orbs;
        });
    }
    /**
     * **Fetch and create orbs from the Docker API**
     * @returns An array of `DOCKER_CONTAINER_ORB` instances
     */
    static fetchDockerOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const dockerData = yield SystemAPI.getDockerContainers();
                //console.log(dockerData)
                return OrbFactory.createOrbsFromData(dockerData, (container) => ({
                    orbType: 'DOCKER_CONTAINER_ORB',
                    label: container.Name.split('_').at(-1).split('.')[0],
                    data: container,
                }));
            }
            catch (error) {
                console.error('Error fetching Docker orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from the registry container based on images*
     * @returns An array of `CONTAINER_IMAGE_ORB` instances
     */
    static fetchRegistryOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const registryData = yield SystemAPI.getRegistryImageInfo();
                return OrbFactory.createOrbsFromData(registryData, (container) => ({
                    orbType: 'CONTAINER_IMAGE_ORB',
                    label: container.displayName,
                    data: container,
                }));
            }
            catch (error) {
                console.error('Error fetching Registry orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from the Docker Swarm**
     * @returns An array of `COMPUTER_ORB` instances
     */
    static fetchDockerSwarmOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const dockerData = yield SystemAPI.getDockerSwarmInfo();
                //console.log(dockerData)
                return OrbFactory.createOrbsFromData(dockerData, (container) => ({
                    orbType: 'COMPUTER_ORB',
                    label: container.Hostname,
                    data: container,
                }));
            }
            catch (error) {
                console.error('Error fetching Docker orbs:', error);
                return [];
            }
        });
    }
    //Currently out of use
    /**
     * **Fetch and create orbs from the Kubernetes API**
     * @returns An array of `KUBERNETES_ORB` instances
     */
    static fetchKubernetesPodOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const kubernetesData = yield SystemAPI.getKubernetesPods();
                return OrbFactory.createOrbsFromData(kubernetesData, (info) => ({
                    orbType: 'KUBERNETES_ORB',
                    label: info.Name,
                    data: info,
                }));
            }
            catch (error) {
                console.error('Error fetching Kubernetes node orbs:', error);
                return [];
            }
        });
    }
    //Currently out of use
    /**
     * **Fetch and create orbs from the Kubernetes API**
     * @returns An array of `COMPUTER_ORB` instances
     */
    static fetchKubernetesNodeOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const kubernetesData = yield SystemAPI.getKubernetesNodes();
                return OrbFactory.createOrbsFromData(kubernetesData, (info) => ({
                    orbType: 'COMPUTER_ORB',
                    label: info.Name,
                    data: info,
                }));
            }
            catch (error) {
                console.error('Error fetching Kubernetes node orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from Desmos**
     * @returns An array of `DESMOS_ORB` instances
     */
    static fetchDesmosOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const desmosData = yield SystemAPI.getDesmosOrbs();
                //console.log("Raw Desmos Data:", desmosData);
                if (!Array.isArray(desmosData)) {
                    console.error("fetchDesmosOrbs: Expected an array but got:", desmosData);
                    return []; // Prevents passing a non-array to `map()`
                }
                return OrbFactory.createOrbsFromData(desmosData, (info) => ({
                    orbType: 'DESMOS_ORB',
                    label: info.title,
                    data: info,
                }));
            }
            catch (error) {
                console.error('Error fetching Kubernetes node orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from container endpoints**
     * @returns An array of `Orb` instances
     */
    static fetchContainerEndpointOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const endpointData = yield SystemAPI.getContainerEndpoints();
                // Data is already in the format we need - no preprocessing required!
                return OrbFactory.createOrbsFromData(endpointData, (endpoint) => {
                    return {
                        orbType: 'CONTAINER_ENDPOINT_ORB',
                        label: endpoint.Label,
                        data: endpoint
                    };
                });
            }
            catch (error) {
                console.error('Error fetching container endpoint orbs:', error);
                return [];
            }
        });
    }
    /**
    * **Fetch and create orbs from the GitHub API**
    * @returns An array of `GITHUB_ORB` instances
    */
    static fetchGitHubOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const repoList = yield SystemAPI.getGitHubRepos();
                if (!Array.isArray(repoList)) {
                    console.error("fetchGitHubOrbs: Expected an array but got:", repoList);
                    return [];
                }
                return OrbFactory.createOrbsFromData(repoList, (repo) => ({
                    orbType: 'GITHUB_ORB',
                    label: repo.name,
                    data: repo
                }));
            }
            catch (error) {
                console.error('Error fetching GitHub orbs:', error);
                return [];
            }
        });
    }
    /**
    * **Fetch and create orbs from owned domains**
    * @returns An array of `DOMAIN_ORB` instances
    */
    static fetchDomainOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const domainList = yield SystemAPI.getDomains();
                if (!Array.isArray(domainList)) {
                    console.error("fetchDomainOrbs: Expected an array but got:", domainList);
                    return [];
                }
                return OrbFactory.createOrbsFromData(domainList, (domain) => ({
                    orbType: 'DOMAIN_ORB',
                    label: domain.name,
                    data: domain
                }));
            }
            catch (error) {
                console.error('Error fetching GitHub orbs:', error);
                return [];
            }
        });
    }
    // new functions
    // ===============================
    /**
     * **Fetch and create orbs from the API**
     * @returns An array of `CHATBOT_ORB` instances
     */
    static fetchChatbotOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // const chatbotData = await SystemAPI.getChatbots();
                const chatbotData = [
                    { Name: "Claude", Model: "claude-3-opus", Provider: "Anthropic" },
                    { Name: "GPT-4", Model: "gpt-4", Provider: "OpenAI" },
                    { Name: "MistralBot", Model: "mistral-7b", Provider: "Local" },
                ];
                return OrbFactory.createOrbsFromData(chatbotData, (info) => ({
                    orbType: 'CHATBOT_ORB',
                    label: info.Name,
                    data: info,
                }));
            }
            catch (error) {
                console.error('Error fetching Chatbot orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from the API**
     * @returns An array of `PROMPT_ORB` instances
     */
    static fetchPromptOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const promptData = yield SystemAPI.getPrompts();
                /*
                const promptData = [
                    { Name: "Fix Code", Description: "Auto-fix broken code snippets" },
                    { Name: "Summarize", Description: "Summarize long documents" },
                    { Name: "Math Solver", Description: "Solve symbolic math problems" },
                ];
                */
                return OrbFactory.createOrbsFromData(promptData, (info) => ({
                    orbType: 'PROMPT_ORB',
                    label: info.id,
                    data: info,
                }));
            }
            catch (error) {
                console.error('Error fetching prompt orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from the Math API**
     * @returns An array of `MATH_ORB` instances
     */
    static fetchMathOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const identityData = yield SystemAPI.getMathOrbs();
                return OrbFactory.createOrbsFromData(identityData, (identity) => {
                    var _a, _b;
                    return ({
                        orbType: 'MATH_ORB',
                        label: (_b = (_a = identity.name) !== null && _a !== void 0 ? _a : identity.Name) !== null && _b !== void 0 ? _b : identity.id,
                        data: identity,
                    });
                });
            }
            catch (error) {
                console.error('Error fetching math orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from the Text Blurbs API**
     * @returns An array of `TEXT_OBJECT_ORB` instances
     */
    static fetchTextObjectOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const textObjectData = yield SystemAPI.getTextObjects();
                return OrbFactory.createOrbsFromData(textObjectData, (blurb) => ({
                    orbType: 'TEXT_OBJECT_ORB',
                    label: blurb.title,
                    data: blurb,
                }));
            }
            catch (error) {
                console.error('Error fetching text object orbs:', error);
                return [];
            }
        });
    }
    /**
 * **Fetch and create orbs from the Database Object API**
 * @returns An array of `DATABASE_OBJECT_ORB` instances
 */
    static fetchDatabaseObjectOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const databaseObjectData = yield SystemAPI.getDatabaseObjects();
                return OrbFactory.createOrbsFromData(databaseObjectData, (blurb) => ({
                    orbType: 'DATABASE_OBJECT_ORB',
                    label: blurb.name,
                    data: blurb,
                }));
            }
            catch (error) {
                console.error('Error fetching database object orbs:', error);
                return [];
            }
        });
    }
    /**
 * **Fetch and create orbs from the Network Gateway API**
 * @returns An array of `NETWORK_GATEWAY_ORB` instances
 */
    static fetchNetworkGatewayOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const networkGatewayData = yield SystemAPI.getNetworkGateways();
                return OrbFactory.createOrbsFromData(networkGatewayData, (blurb) => ({
                    orbType: 'NETWORK_GATEWAY_ORB',
                    label: blurb.name,
                    data: blurb,
                }));
            }
            catch (error) {
                console.error('Error fetching text object orbs:', error);
                return [];
            }
        });
    }
    /**
     * **Fetch and create orbs from a language-based API**
     * - Determines orb type based on `language` field
     * @returns An array of orbs corresponding to different languages
     */
    static fetchLanguageOrbs() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const entries = yield SystemAPI.getLanguageOrbs();
                /*
                if (!Array.isArray(entries)) {
                    console.warn("fetchLanguageOrbs: Non-array response", entries);
                    return [];
                }
                */
                return OrbFactory.createOrbsFromData(entries, item => {
                    var _a;
                    return ({
                        orbType: OrbFactory.mapLanguageToType(((_a = item.Language) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || 'api'),
                        label: item.ObjectName || 'Unnamed Object',
                        data: item,
                    });
                });
            }
            catch (error) {
                console.error('fetchLanguageOrbs error:', error);
                return [];
            }
        });
    }
    /**
  * **Fetch and create orbs from a single repo**
  * - Determines orb type based on `language` field
  * @returns An array of orbs corresponding to different languages
  */
    static fetchLanguageOrbsByRepo(repoName) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const entries = yield SystemAPI.getLanguageOrbsByRepo(repoName);
                if (!Array.isArray(entries)) {
                    console.warn("fetchLanguageOrbsByRepo: Non-array response", entries);
                    return [];
                }
                return OrbFactory.createOrbsFromData(entries, item => {
                    var _a;
                    return ({
                        orbType: OrbFactory.mapLanguageToType(((_a = item.Language) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || 'api'),
                        label: item.ObjectName || 'Unnamed Object',
                        data: item,
                    });
                });
            }
            catch (error) {
                console.error('fetchLanguageOrbsByRepo error:', error);
                return [];
            }
        });
    }
    /**
     * **Map programming languages to predefined orb types**
     * @param language - The programming language name
     * @returns The corresponding orb type string
     */
    static mapLanguageToType(language) {
        switch (language) {
            case 'typescript':
                return 'TYPESCRIPT_ORB';
            case 'javascript':
                return 'CLASSIC';
            case 'python':
                return 'PYTHON_ORB';
            case 'bash':
                return 'BASH_ORB';
            case 'java':
                return 'JAVA_ORB';
            case 'go':
                return 'GO_ORB';
            case 'rust':
                return 'RUST_ORB';
            case 'powershell':
                return 'POWERSHELL_ORB';
            case 'c':
            case 'c++':
                return 'CLASSIC'; // Or define custom for C/C++
            default:
                return 'API_ORB';
        }
    }
    //Unused
    // Read-only accessor method
    static getOrbConfig(orbType) {
        if (!orbType) {
            console.warn("getOrbConfig called with undefined orbType!");
            return undefined;
        }
        const config = OrbTypes[orbType];
        if (!config) {
            console.warn(`Orb config not found for type: ${orbType}`);
            return undefined;
        }
        return config;
    }
    /**
     * **Fetch and create orbs for all known orb types**
     * @returns An array of `Orb` instances
     */
    static fetchOrbTypesOld() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fullOrbTypes = OrbFactory.getFullOrbTypes();
                return OrbFactory.createOrbsFromData(fullOrbTypes, ({ name, config }) => ({
                    orbType: config,
                    label: name,
                    data: { type: name } // Store metadata about the orb type
                }));
            }
            catch (error) {
                console.error('Error fetching orb types:', error);
                return [];
            }
        });
    }
    static fetchOrbTypes() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const fullOrbTypes = OrbFactory.getFullOrbTypes();
                return OrbFactory.createOrbsFromData(fullOrbTypes, ({ name, config }) => {
                    // Reverse-lookup the OrbTypes key by comparing object references
                    const orbTypeEntry = Object.entries(OrbTypes).find(([, value]) => value === config);
                    if (!orbTypeEntry) {
                        console.warn("Orb config not found in OrbTypes:", { name, config });
                        return { orbType: "UNKNOWN_ORB", label: name, data: { type: name } };
                    }
                    const [orbTypeKey] = orbTypeEntry;
                    return {
                        orbType: orbTypeKey,
                        label: name,
                        data: { type: orbTypeKey }
                    };
                });
            }
            catch (error) {
                console.error("Error fetching orb types:", error);
                return [];
            }
        });
    }
    static getFullOrbTypes() {
        return [
            { name: "Bash Orb", config: OrbTypes.BASH_ORB },
            { name: "Java Orb", config: OrbTypes.JAVA_ORB },
            { name: "Python Orb", config: OrbTypes.PYTHON_ORB },
            { name: "Powershell Orb", config: OrbTypes.POWERSHELL_ORB },
            { name: "Go Orb", config: OrbTypes.GO_ORB },
            { name: "Rust Orb", config: OrbTypes.RUST_ORB },
            { name: "JavaScript Orb", config: OrbTypes.JAVASCRIPT_ORB },
            { name: "TypeScript Orb", config: OrbTypes.TYPESCRIPT_ORB },
            { name: "Ansible Orb", config: OrbTypes.ANSIBLE_ORB },
            { name: "Math Orb", config: OrbTypes.MATH_ORB },
            { name: "Desmos Orb", config: OrbTypes.DESMOS_ORB },
            { name: "Github Orb", config: OrbTypes.GITHUB_ORB },
            { name: "Docker Container Orb", config: OrbTypes.DOCKER_CONTAINER_ORB },
            { name: "Endpoint Orb", config: OrbTypes.CONTAINER_ENDPOINT_ORB },
            //{ name: "Kubernetes Orb", config: OrbTypes.KUBERNETES_ORB },
            { name: "Computer Orb", config: OrbTypes.COMPUTER_ORB },
            { name: "Prompt Orb", config: OrbTypes.PROMPT_ORB },
            { name: "Prompt Response Orb", config: OrbTypes.PROMPT_RESPONSE_ORB },
            { name: "AI Agent Orb", config: OrbTypes.AI_AGENT_ORB },
            { name: "System Will Orb", config: OrbTypes.SYSTEM_WILL_ORB },
            { name: "Text Information Orb", config: OrbTypes.TEXT_INFO_ORB },
        ];
    }
    static createMiniOrbByTypeOld(orbType, labelText, jsonData = {}, fixedScaleFactor = 0.16, // slightly thicker than 0.1 so layers are still visible when tiny
    fixedRadius = 12 // a practical mini radius (caller can override)
    ) {
        const baseConfig = OrbTypes[orbType];
        if (!baseConfig)
            return;
        // --- text tuning for minis ---
        // Render text at higher pixel size, then scale the sprite down → crisper edges
        const fontPx = Math.max(16, Math.min(36, Math.round(fixedRadius * 1.6))); // 16–36px
        const outlinePx = Math.max(1, Math.round(fontPx * 0.07)); // ~1–3px
        const spriteScale = 1.4; // small on-screen size, but oversampled
        const hex6 = (n, fallback = '#ffffff') => (typeof n === 'number' ? `#${n.toString(16).padStart(6, '0')}` : fallback);
        // Always ensure outline contrasts with fill for tiny labels
        const fill = hex6(baseConfig.textColor, '#ffffff');
        const outline = '#111111';
        const config = Object.assign(Object.assign({}, baseConfig), { radius: fixedRadius, defaultLayerScaleFactor: fixedScaleFactor, textCanvasScale: spriteScale, textFont: `${fontPx}px Arial, sans-serif`, textColor: parseInt(fill.replace('#', ''), 16), textOutlineColor: parseInt(outline.replace('#', ''), 16), layers: baseConfig.layers.map(layer => (Object.assign(Object.assign({}, layer), { scaleFactor: fixedScaleFactor }))) });
        if (labelText) {
            config.text = TextCreation.createTextSprite(labelText, config.textCanvasScale, // on-screen scale (small)
            undefined, outlinePx, // thin outline to avoid blob
            config.textFont, // high pixel font for crispness
            fill, // high-contrast fill
            outline // dark outline
            );
        }
        return new Orb(config, jsonData, labelText);
    }
    // Measure the outermost radius from the group's origin using geometry boundingSpheres.
    // Ignores sprites by default so labels/images don't affect size.
    static _measureOuterRadius(group, includeSprites = false) {
        if (!group)
            return 0;
        // Ensure world transforms are fresh
        group.updateWorldMatrix(true, true);
        // Group/world origin
        const groupOriginWS = new THREE.Vector3();
        group.getWorldPosition(groupOriginWS);
        let maxR = 0;
        const scaleV = new THREE.Vector3();
        const centerWS = new THREE.Vector3();
        group.traverse((child) => {
            if (!child)
                return;
            if (child.isSprite && !includeSprites)
                return;
            const geo = child.geometry;
            if (!geo)
                return;
            // Compute / read bounding sphere in LOCAL space
            if (!geo.boundingSphere)
                geo.computeBoundingSphere();
            const bs = geo.boundingSphere;
            if (!bs)
                return;
            // Transform center to world space
            centerWS.copy(bs.center);
            child.localToWorld(centerWS);
            // Extract world scale; radius scales by the max component
            scaleV.setFromMatrixScale(child.matrixWorld);
            const scale = Math.max(scaleV.x, scaleV.y, scaleV.z);
            const rWorld = bs.radius * scale;
            const d = centerWS.distanceTo(groupOriginWS);
            // Concentric layers -> union radius is distance-to-center + local radius
            const candidate = d + rWorld;
            if (candidate > maxR)
                maxR = candidate;
        });
        return maxR;
    }
    // Normalize the entire orb group to a target world-space radius.
    // Returns the applied scale (1.0 means no change) or null if measurement failed.
    static normalizeOrbSize(orb, targetRadius, opts = {}) {
        const group = orb.group;
        if (!group)
            return null;
        const includeSprites = !!opts.includeSprites;
        const currentR = this._measureOuterRadius(group, includeSprites);
        if (currentR <= 0)
            return null;
        const s = targetRadius / currentR;
        group.scale.multiplyScalar(s);
        group.updateWorldMatrix(true, true);
        return s;
    }
    static createMiniOrbByType(orbType, labelText, jsonData = {}, fixedScaleFactor = 0.16, fixedRadius = 12) {
        const baseConfig = OrbTypes[orbType];
        if (!baseConfig)
            return;
        // --- crisp micro text ---
        const fontPx = Math.max(16, Math.min(36, Math.round(fixedRadius * 1.6)));
        const outlinePx = Math.max(1, Math.round(fontPx * 0.07));
        const spriteScale = 1.4;
        const hex6 = (n, fallback = '#ffffff') => (typeof n === 'number' ? `#${n.toString(16).padStart(6, '0')}` : fallback);
        const fill = hex6(baseConfig.textColor, '#ffffff');
        const outline = '#111111';
        const config = Object.assign(Object.assign({}, baseConfig), { radius: fixedRadius, defaultLayerScaleFactor: fixedScaleFactor, textCanvasScale: spriteScale, textFont: `${fontPx}px Arial, sans-serif`, textColor: parseInt(fill.replace('#', ''), 16), textOutlineColor: parseInt(outline.replace('#', ''), 16), layers: baseConfig.layers.map(layer => (Object.assign(Object.assign({}, layer), { 
                // absolute layer thickness at mini scale
                scaleFactor: fixedScaleFactor }))) });
        if (labelText) {
            config.text = TextCreation.createTextSprite(labelText, config.textCanvasScale, undefined, outlinePx, config.textFont, fill, outline);
        }
        const orb = new Orb(config, jsonData, labelText);
        const group = orb.group;
        if (!group)
            return orb;
        // One immediate attempt (for types that build synchronously)…
        OrbFactory.normalizeOrbSize(orb, fixedRadius, { includeSprites: false });
        // …then a couple more frames after it's in the scene (handles lazy attachments)
        let tries = 0;
        const retry = () => {
            tries++;
            const applied = OrbFactory.normalizeOrbSize(orb, fixedRadius, { includeSprites: false });
            if (tries < 3)
                requestAnimationFrame(retry);
        };
        // If it already has children, try next frame.
        if (group.children.length)
            requestAnimationFrame(retry);
        // Also normalize right after the group is added anywhere
        if (group.addEventListener) {
            group.addEventListener('added', () => {
                // normalize on next two frames to catch just-attached meshes
                requestAnimationFrame(retry);
            });
        }
        return orb;
    }
}
